import React, { useCallback, useEffect } from "react";
import { useStores } from "../../hooks/useStores";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { Container } from "../../components/Container";
import { Text } from "@chakra-ui/layout";
import {
  Tabs,
  TabList,
  Tab,
  TabPanels,
  HStack,
  IconButton,
  Stack,
  TabPanel,
  Spinner,
  Button,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useHistory, useParams } from "react-router-dom";
import { TabClient } from "./TabClient";
import { TabSubscription } from "./TabSubscription";
import { TabObjective } from "./TabObjective";
import { TabEvaluations } from "./TabEvaluations";
import { TabComunications } from "./TabComunications";
import { TabEmail } from "./TabEmail";
import { Icon } from "../../components/Icon";
import TabActivities from "./TabActivities";
import { DeleteCustomerAlertDialog } from "../../components/DeleteCustomerAlertDialog";

interface RouteParams {
  userId: string;
}

export const ClientDetail: React.FC = observer(() => {
  const { clientsStore } = useStores();
  const { t } = useTranslation();
  const { userId } = useParams<RouteParams>();
  const history = useHistory();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  useEffect(() => {
    if (userId) {
      clientsStore.getClient(userId);
    }
  }, [clientsStore, userId]);

  const handleDeleteCustomer = useCallback(async () => {
    try {
      if (userId) {
        await clientsStore.deleteClient(userId);
        toast({
          status: "success",
          title: t("common.actionExecutedSuccessfully"),
        });
        history.goBack();
      }
    } catch (err) {
      window.alert(err);
    }
  }, [clientsStore, history, t, toast, userId]);

  return (
    <Stack margin={"2rem"} gridGap={"2rem"}>
      <HStack justifyContent="space-between">
        <HStack>
          <IconButton
            aria-label="Back"
            icon={<Icon iconName={"HiArrowLeft"} />}
            variant={"ghost"}
            onClick={() => history.goBack()}
          />
          <Stack>
            <Text fontSize="xl">{t("screens.clients.title")}</Text>
            <Text fontSize="3xl" fontWeight={300}>
              {clientsStore.customerData ? (
                `${clientsStore.customerData.surname} ${clientsStore.customerData.name}`
              ) : (
                <Spinner color="purple.500" size="md" />
              )}
            </Text>
          </Stack>
        </HStack>
        <Button colorScheme="red" variant="solid" onClick={onOpen}>
          {t("screens.client.deleteCustomer")}
        </Button>
      </HStack>

      <Container>
        <Tabs variant="soft-rounded" colorScheme="purple" size="md" isFitted>
          <TabList>
            <Tab>{t("screens.client.tabs.profile")}</Tab>
            <Tab>{t("screens.client.tabs.subscription")}</Tab>
            <Tab>{t("screens.client.tabs.objectives")}</Tab>
            <Tab>{t("screens.client.tabs.evaluations")}</Tab>
            <Tab>{t("screens.client.tabs.trainings")}</Tab>
            <Tab>{t("screens.client.tabs.comunications")}</Tab>
            <Tab>{t("screens.client.tabs.emails")}</Tab>
          </TabList>
          <TabPanels>
            <TabPanel>
              <TabClient />
            </TabPanel>
            <TabPanel>
              <TabSubscription />
            </TabPanel>
            <TabPanel>
              <TabObjective />
            </TabPanel>
            <TabPanel>
              <TabEvaluations />
            </TabPanel>
            <TabPanel>
              <TabActivities />
            </TabPanel>
            <TabPanel>
              <TabComunications />
            </TabPanel>
            <TabPanel>
              <TabEmail />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Container>

      <DeleteCustomerAlertDialog
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={handleDeleteCustomer}
      />
    </Stack>
  );
});
